import { useMemo, useState, useRef, useEffect } from 'react';
import { Form } from 'react-bulma-components';
import className from 'classnames';
import { Link } from '@reach/router';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';

import './input.scss';

const CustomInput = (props) => {
    const { inputClass, label, name, autoComplete, placeholder, type, componentType = 'input', showPasswordMeter, checked, value, onChange, readOnly, errors, warnings, isRequired, extraAction, disabled, autoFocus, id, options = [], multiple = false, isHtmlLabel = true } = props;
    const { Field, Control, Label, Input, Checkbox } = Form;
    const [inputType, setInputType] = useState(type);
    const [selectedValues, setSelectedValues] = useState([]);
    const inputRef = useRef(null);

    const revealPasswordClassName = className({
        'revealed': inputType === 'text'
    }, 'reveal');

    const errorsWrapperClassName = className({
        'errors--wrapper-active': (typeof errors !== 'undefined' && errors?.length > 0) || (typeof warnings !== 'undefined' && warnings?.length > 0),
    }, 'errors--wrapper');

    const fieldClassName = className({
        'input--custom-error': typeof errors !== 'undefined' && errors?.length > 0
    }, 'input--custom');

    const errorsDom = useMemo(() => {
        if (errors?.length) {
            return errors.map((error, index) => {
                return <li key={index}>
                    {error}
                </li>;
            });
        } else if (warnings?.length) {
            return warnings.map((warning, index) => {
                return <li key={index} className='is-warning'>
                    {warning}
                </li>;
            });
        }

    }, [errors, warnings]);

    const revealPassword = (e) => {
        e.preventDefault();
        if (inputType === 'password') {
            setInputType('text');
        } else {
            setInputType('password');
        }
        if (inputRef?.current) {
            inputRef.current.focus();
        }
    };

    const extraActionDom = useMemo(() => {
        if (!extraAction) {
            return null;
        }

        return <Link to={extraAction?.to} state={{ email: extraAction?.value }} className='input--custom-reset-password'>
            {extraAction?.label}
        </Link>;
    }, [extraAction]);

    const revealPasswordDom = type === 'password' ? <span className={revealPasswordClassName} onClick={revealPassword}/> : '';

    const passwordMeter = useMemo(() => {
        if (!showPasswordMeter || !value) {
            return null;
        }

        let meters = ['strong', 'strong', 'strong'];
        if (errors?.length) {
            meters = ['week', 'none', 'none'];
        } else if (warnings?.length) {
            meters = ['medium', 'medium', 'none'];
        }

        return <div className='passwordMeter'>
            {meters.map((meter, index) => <div key={index} className={meter}/>)}
        </div>;
    }, [showPasswordMeter, errors, warnings, value]);

    useEffect(() => {
        if (autoFocus) {
            if (inputRef?.current) {
                inputRef.current.focus();
            }
        }
    });

    const handleRemoveItem = ( selectProps, data ) => {
        setSelectedValues((prev) => prev.filter((item) => item.id !== data.id));
        const { name } = selectProps;
        onChange({ target: { name, value: selectedValues.filter((item) => item.id !== data.id) } });
    };

    const MultiValueContainer = ({ selectProps, data }) => {
        const { value } = selectProps;
        const selectedValues = value;
        if (selectedValues.length > 3) {
            if (selectedValues.indexOf(data) === 3) {
                return (
                    <div style={{ padding: '2px 8px 1px 8px', height: '25px', background: '#E9EAEB', fontSize: '13px' }}>
                        {selectedValues.length > 3 && `${selectedValues.length - 3} + More`}
                    </div>
                );
            } else if (selectedValues.indexOf(data) >= 3) {
                return null;
            }
        }
        return (
            <components.MultiValueContainer {...selectProps}>
                <div style={{ padding: '2px 8px 1px 8px', height: '25px', background: '#E9EAEB', fontSize: '13px' }}>
                    {data.name}
                    <span style={{ cursor: 'pointer', marginLeft: '10px', height: '10px', marginBottom: '10px' }}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleRemoveItem(selectProps, data); } }>x</span>
                </div>
            </components.MultiValueContainer>
        );
    };


    const customOnChange = (isMultiple, values) => {
        if (!isMultiple) {
            return;
        }
        setSelectedValues(values);
    };
    const inputTypeDom = <>
        {label && <Label id={id || name} className={className('input--label', { required: isRequired })}>{label}</Label>}
        <Input aria-labelledby={id || name} domRef={inputRef} className={inputClass} disabled={disabled} placeholder={placeholder || ''} name={name} type={inputType} value={value} onChange={onChange} readOnly={!onChange || readOnly} required={isRequired} onKeyPress={onChange} autoComplete={autoComplete || name}/>
        {revealPasswordDom}
        {passwordMeter}
        {
            errors ?
                <ul className={errorsWrapperClassName}>
                    {errorsDom}
                </ul> :
                ''
        }
    </>;

    const checkboxTypeDom = (
        <Checkbox className={inputClass} name={name} checked={checked} onChange={onChange} disabled={!!readOnly}>
            {isHtmlLabel ? <span dangerouslySetInnerHTML={{ __html: label }} /> : <span>{label}</span>}
        </Checkbox>
    );

    const selectTypeDom = (
        <>
            {label && (
                <Label
                    id={id || name}
                    className={className('input--label', { required: isRequired })}
                >
                    {label}
                </Label>
            )} {
                multiple ? (<Select
                    isClearable
                    name={name}
                    options={options}
                    value={selectedValues}
                    classNamePrefix='customSelect'
                    isMulti={multiple}
                    placeholder=''
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.name}
                    onChange={(value) => {
                        customOnChange(multiple, value);
                        onChange({ target: { name, value } }); }
                    }
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    hideSelectedOptions={false}
                    components={{ MultiValueContainer }}
                    className={inputClass}
                />) : (
                    <Select
                        isClearable
                        name={name}
                        options={options}
                        classNamePrefix='customSelect'
                        placeholder=''
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.name}
                        onChange={(value) => {
                            onChange({ target: { name, value } }); }
                        }
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                )
            }
            {errors ? <ul className={errorsWrapperClassName}>{errorsDom}</ul> : ''}
        </>
    );

    const innerField = componentType === 'input' ? inputTypeDom : componentType === 'select' ? selectTypeDom : checkboxTypeDom;

    return <Field className={fieldClassName}>
        <Control>
            {innerField}
        </Control>
        {extraActionDom}
    </Field>;
};

export default CustomInput;

CustomInput.propTypes = {
    inputClass: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    name: PropTypes.string,
    autoComplete: PropTypes.any,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    componentType: PropTypes.string,
    showPasswordMeter: PropTypes.bool,
    checked: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    errors: PropTypes.array,
    warnings: PropTypes.array,
    isRequired: PropTypes.bool,
    extraAction: PropTypes.object,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    id: PropTypes.string,
    options: PropTypes.array,
    multiple: PropTypes.bool,
    selectProps: PropTypes.object,
    data: PropTypes.object,
    isHtmlLabel: PropTypes.bool
};
